/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { api } from '../services/api';
import {
  computeAfterSpeechDelayMs,
  formatLanguage,
  formatLatencyMs,
  formatTranslationStatus,
  parseEventTimestamp,
  getTranslationStatusColor,
} from '../utils/translationUi';

const parseJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeAction = (action) => {
  if (!action) return '-';
  return String(action).replace(/_EXECUTED$/, '');
};

const getActionColor = (action) => {
  const normalized = normalizeAction(action);
  if (normalized === 'BLOCK' || normalized === 'DROP') return 'error';
  if (normalized === 'WHISPER') return 'warning';
  if (normalized === 'ALLOW' || normalized === 'LOG_ONLY') return 'success';
  return 'default';
};

const formatMs = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)} ms`;
};

const formatDurationMs = (value) => {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return '-';
  const totalSeconds = Math.round(duration / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${seconds}s`;
};

const formatRelativeMs = (value) => {
  const duration = Number(value);
  if (!Number.isFinite(duration)) return '-';
  const sign = duration >= 0 ? '+' : '';
  if (Math.abs(duration) >= 1000) {
    return `${sign}${(duration / 1000).toFixed(2)}s`;
  }
  return `${sign}${Math.round(duration)}ms`;
};

const formatEstimatedUsd = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  if (amount === 0) return '$0.0000';
  return amount < 0.01 ? `$${amount.toFixed(6)}` : `$${amount.toFixed(4)}`;
};

const formatUsageQuantity = (stage) => {
  if (!stage) return '-';
  const quantity = Number(stage.quantity);
  if (!Number.isFinite(quantity)) return '-';
  if (stage.billingUnit === 'audio_ms') {
    return `${(quantity / 60000).toFixed(2)} min`;
  }
  return `${quantity.toLocaleString()} chars`;
};

const formatVoiceProfile = (value) => {
  if (value === 'male') return 'Male';
  if (value === 'female') return 'Female';
  return 'Unknown';
};

const formatThailandTime = (timestamp) => {
  if (!timestamp) return '-';
  const timestampString = typeof timestamp === 'string' ? timestamp : new Date(timestamp).toISOString();
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(timestampString);
  const safeTimestamp = hasTimezone ? timestampString : `${timestampString}Z`;
  const date = new Date(safeTimestamp);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

const formatMeasurementTimestamp = (timestamp, firstTimestamp = null) => {
  if (!timestamp) return '-';
  const relative = typeof firstTimestamp === 'number'
    ? ` (${formatRelativeMs(timestamp - firstTimestamp)})`
    : '';
  return `${formatThailandTime(timestamp)}${relative}`;
};

const parseTraceTime = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const buildSipDirectionAndProtocol = (eventName, details) => {
  switch (eventName) {
    case 'CALL_SETUP_REQUEST':
      return { direction: 'API → Simulator Gateway', protocol: 'Call setup' };
    case 'CALL_INITIATED':
      return { direction: 'Web UI → API Backend', protocol: 'SIP' };
    case 'CALL_COMPLETED':
    case 'CALL_COMPLETION':
      return { direction: 'SIP Server → API', protocol: 'SIP' };
    case 'INCOMING_SIP_EVENT':
      return { direction: 'SIP Server → Rhino', protocol: details?.method ? `SIP ${details.method}` : 'SIP' };
    case 'B2BUA_ACTION':
    case 'B2BUA_STATE_CHANGE':
    case 'SIP_HEADER_MOD':
      return { direction: 'Rhino ↔ SIP Server', protocol: 'SIP/B2BUA' };
    default:
      return { direction: 'Rhino ↔ SIP Server', protocol: details?.method ? `SIP ${details.method}` : 'SIP/B2BUA' };
  }
};

const buildSipDetails = (eventName, row, details) => {
  if (eventName === 'CALL_SETUP_REQUEST') {
    const from = details?.fromNumber || details?.from || row.callerNumber || '-';
    const to = details?.toNumber || details?.to || row.calleeNumber || '-';
    return {
      primary: `${from} → ${to}`,
      secondary: row.message || 'Call initiated (mock mode)',
    };
  }

  if (eventName === 'INCOMING_SIP_EVENT') {
    return {
      primary: `${details?.method || 'INVITE'} ${details?.from || row.callerNumber || '-'} → ${details?.to || row.calleeNumber || '-'}`,
      secondary: row.message || 'Event logged',
    };
  }

  if (eventName === 'B2BUA_STATE_CHANGE') {
    return {
      primary: `Leg: ${details?.leg || 'AB'} | State: ${details?.state || row.action || 'UNKNOWN'}`,
      secondary: details?.state || row.message || '',
    };
  }

  return {
    primary: row.action || row.message || details?.state || details?.reason || 'Event logged',
    secondary: row.message && row.action ? row.message : '',
  };
};

const formatTraceEvent = (eventName) => {
  return eventName || 'TRACE_EVENT';
};

const buildTasDetails = (row, details, serviceContext) => {
  const parts = [];
  if (row.message) parts.push(row.message);
  if (row.action) parts.push(`Action: ${row.action}`);
  if (details?.decision) parts.push(`Decision: ${details.decision}`);
  if (details?.state) parts.push(`State: ${details.state}`);
  if (details?.leg) parts.push(`Leg: ${details.leg}`);
  if (details?.endpoint) parts.push(`Endpoint: ${details.endpoint}`);
  if (serviceContext?.policy || details?.policy) parts.push(`Policy: ${serviceContext?.policy || details?.policy}`);
  if (typeof row.durationMs === 'number') parts.push(`Duration: ${row.durationMs}ms`);
  if (details?.from || details?.to) parts.push(`From: ${details?.from || '-'} To: ${details?.to || '-'}`);
  return parts.join(' | ') || 'Event logged';
};

const buildTranslationTimelineDetails = (row) => {
  const details = row.details || {};
  const parts = [];
  if (row.message) parts.push(row.message);
  if (details.status) parts.push(`Status: ${formatTranslationStatus(details.status)}`);
  if (details.skipReason) parts.push(`Reason: ${formatTranslationStatus(details.skipReason)}`);
  if (details.detectedSourceLanguage) parts.push(`Detected: ${details.detectedSourceLanguage}`);
  if (details.targetLanguage) parts.push(`Target: ${details.targetLanguage}`);
  if (details.audioMode) parts.push(`Mode: ${formatTranslationStatus(details.audioMode)}`);
  if (details.provider) parts.push(`Provider: ${details.provider}`);
  if (details.sessionId) parts.push(`Session: ${details.sessionId}`);
  if (details.sequenceNo !== undefined) parts.push(`Seq: ${details.sequenceNo}`);
  if (details.chunkBytes !== undefined) parts.push(`Chunk: ${details.chunkBytes} bytes`);
  if (details.durationMs !== undefined) parts.push(`Audio: ${details.durationMs}ms`);
  if (details.bufferedDurationMs !== undefined && details.bufferedDurationMs !== null) parts.push(`Buffered: ${details.bufferedDurationMs}ms`);
  if (details.audioRms !== undefined) parts.push(`RMS: ${details.audioRms}`);
  if (details.audioPeak !== undefined) parts.push(`Peak: ${details.audioPeak}`);
  if (details.likelySilence === true) parts.push('Likely silence');
  if (details.sttStreamLagMs !== undefined) parts.push(`Lag: ${details.sttStreamLagMs}ms`);
  if (details.sttStreamLatencyMs !== undefined) parts.push(`STT total: ${details.sttStreamLatencyMs}ms`);
  if (details.interimTranscript) parts.push(`Interim: ${details.interimTranscript}`);
  if (details.ttsStartedAt) parts.push(`TTS start: ${details.ttsStartedAt}`);
  if (details.externalHost) parts.push(`RTP: ${details.externalHost}`);
  if (details.audioUri) parts.push(`Audio: ${details.audioUri}`);
  if (details.fileName) parts.push(`File: ${details.fileName}`);
  return parts.join(' | ') || 'Event logged';
};

export default function SipTraceViewer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [traceId, setTraceId] = useState('');
  const [traceRows, setTraceRows] = useState([]);
  const [callRecord, setCallRecord] = useState(null);
  const [translationStatus, setTranslationStatus] = useState(null);
  const [translationTranscript, setTranslationTranscript] = useState(null);
  const [translationTranscriptError, setTranslationTranscriptError] = useState(null);
  const [detailTab, setDetailTab] = useState('timeline');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('traceId') || '';
    setTraceId(id);

    if (!id) {
      setError('Missing traceId in URL.');
      setLoading(false);
      return;
    }

    Promise.allSettled([
      api.getRhinoTrace(id),
      api.getCallHistoryRecord(id),
      api.getTranslationSessionStatusByCall(id),
    ])
      .then(async ([traceResult, recordResult, translationResult]) => {
        if (traceResult.status === 'fulfilled') {
          const rows = Array.isArray(traceResult.value)
            ? traceResult.value
            : (traceResult.value?.events || []);
          setTraceRows(rows);
        } else {
          throw traceResult.reason;
        }

        if (recordResult.status === 'fulfilled') {
          setCallRecord(recordResult.value || null);
        }

        if (translationResult.status === 'fulfilled') {
          setTranslationStatus(translationResult.value || null);
        }

        if (translationResult.status === 'fulfilled' || recordResult.status === 'fulfilled') {
          try {
            const transcript = await api.getTranslationTranscriptByCall(id);
            setTranslationTranscript(transcript || null);
            setTranslationTranscriptError(null);
          } catch (transcriptError) {
            setTranslationTranscript(null);
            setTranslationTranscriptError(
              transcriptError?.response?.data?.error ||
              transcriptError?.message ||
              'Translation transcript is not available for this call.'
            );
          }
        }
      })
      .catch((err) => {
        const message = err?.response?.data?.error || err?.message || 'Failed to load trace.';
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const normalizedRows = useMemo(() => {
    return traceRows.map((row) => {
      const details = parseJson(row.details);
      const serviceContext = parseJson(row.serviceContext);
      const eventName = row.traceStep || row.event || 'TRACE';
      const timestamp = parseEventTimestamp(row.timestamp);
      return {
        ...row,
        eventName,
        timestamp,
        details,
        serviceContext,
      };
    });
  }, [traceRows]);

  const firstTimestamp = useMemo(() => {
    return normalizedRows.find((row) => typeof row.timestamp === 'number')?.timestamp ?? null;
  }, [normalizedRows]);

  const callerDisclaimerPlayback = useMemo(() => {
    for (let index = normalizedRows.length - 1; index >= 0; index -= 1) {
      const eventName = normalizedRows[index].eventName;
      if (eventName === 'MEDIA_CALLER_RECORDING_DISCLAIMER_COMPLETED') {
        return { label: 'Disclaimer Played', color: 'success' };
      }
      if (eventName === 'MEDIA_CALLER_RECORDING_DISCLAIMER_FAILED') {
        return { label: 'Disclaimer Failed', color: 'warning' };
      }
      if (eventName === 'MEDIA_CALLER_RECORDING_DISCLAIMER_STARTED') {
        return { label: 'Disclaimer Playing', color: 'info' };
      }
    }
    return null;
  }, [normalizedRows]);

  const tasTraceRows = useMemo(() => {
    return normalizedRows
      .filter((row) => {
        const source = row.details?.source;
        const simEvents = ['CALL_SETUP_REQUEST', 'CALL_INITIATED', 'CALL_COMPLETED'];
        return source === 'rhinoTrace' || !simEvents.includes(row.eventName);
      })
      .map((row) => {
      const relativeMs = typeof row.timestamp === 'number' && typeof firstTimestamp === 'number'
        ? row.timestamp - firstTimestamp
        : null;
      const payload = row.details || row.serviceContext || null;
      return {
        id: row.traceId || `${row.eventName}-${row.timestamp}`,
        relativeMs,
        event: formatTraceEvent(row.eventName),
        status: row.status || 'INFO',
        details: buildTasDetails(row, row.details, row.serviceContext),
        payload,
      };
    });
  }, [normalizedRows, firstTimestamp]);

  const translationTimelineRows = useMemo(() => {
    return normalizedRows
      .filter((row) => (
        row.eventName?.startsWith('TRANSLATION_') ||
        row.eventName?.startsWith('MEDIA_AUDIO_') ||
        row.eventName?.startsWith('MEDIA_PLAYBACK_') ||
        row.eventName?.startsWith('MEDIA_STT_') ||
        row.eventName?.startsWith('MEDIA_TTS_') ||
        row.eventName?.startsWith('MEDIA_LIVE_RTP_') ||
        row.eventName?.startsWith('MEDIA_CALLER_RECORDING_DISCLAIMER_') ||
        row.eventName?.startsWith('MEDIA_TRANSLATION_')
      ))
      .map((row) => ({
        id: row.traceId || `${row.eventName}-${row.timestamp}`,
        relativeMs: typeof row.timestamp === 'number' && typeof firstTimestamp === 'number'
          ? row.timestamp - firstTimestamp
          : null,
        event: formatTraceEvent(row.eventName),
        status: row.status || 'INFO',
        details: buildTranslationTimelineDetails(row),
      }));
  }, [normalizedRows, firstTimestamp]);

  const timingMeasurements = useMemo(() => {
    const legFromDetails = (details) => details?.direction === 'callee_to_caller' ? 'callee' : 'caller';
    const rowKey = (leg, seq) => `${leg}-${seq}`;
    const endOfTurnByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_END_OF_TURN_PENDING' && row.details?.sequenceNo != null)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), row.timestamp])
    );
    const transcriptReleasedByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.transcriptEmittedAt) ?? row.timestamp
        ])
        .filter(([, releasedAt]) => Number.isFinite(releasedAt))
    );
    const speechActivityEndedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.speechActivityEndedAt)
        ])
        .filter(([, endedAt]) => Number.isFinite(endedAt))
    );
    const translationCompletedByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_TRANSLATION_COMPLETED' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.translationCompletedAt) ?? row.timestamp
        ])
    );
    // Older traces predate MEDIA_TRANSLATION_COMPLETED. Playback queueing happens
    // immediately after translation storage, so it is the closest historical marker.
    const queuedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_PLAYBACK_QUEUED' && row.details?.sequenceNo != null)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), row.timestamp])
    );
    const translationMeasurements = Array.from(transcriptReleasedByKey.entries()).reduce((acc, [key, releasedAt]) => {
      const endedAt = speechActivityEndedAtByKey.get(key) ?? endOfTurnByKey.get(key) ?? releasedAt;
      const completedAt = translationCompletedByKey.get(key) ?? queuedAtByKey.get(key);
      if (Number.isFinite(endedAt) && Number.isFinite(completedAt) && completedAt >= endedAt) {
        acc.push({ key, endedAt, completedAt, delayMs: completedAt - endedAt });
      }
      return acc;
    }, []);

    // Conversation-start timing remains speech start -> first audible AI2 RTP.
    const speechStartByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.callerSpeechStartedAt) ??
            parseTraceTime(row.details.receivedAt) ??
            parseTraceTime(row.details.transcriptEmittedAt)
        ])
        .filter(([, speechStartedAt]) => Number.isFinite(speechStartedAt))
    );
    const firstRtpByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_TTS_FIRST_RTP_PACKET_SENT' && row.details?.sequenceNo != null && row.details?.firstRtpPacketSentAt)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), parseTraceTime(row.details.firstRtpPacketSentAt)])
    );
    const firstAudioByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_TTS_FIRST_AUDIO_CHUNK' && row.details?.sequenceNo != null && row.details?.firstAudioChunkAt)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), parseTraceTime(row.details.firstAudioChunkAt)])
    );
    const ai2AudioMeasurements = Array.from(speechStartByKey.entries()).reduce((acc, [key, startedAt]) => {
      const chunkAt = firstRtpByKey.get(key) ?? firstAudioByKey.get(key);
      if (Number.isFinite(chunkAt) && chunkAt > startedAt) {
        acc.push({ key, startedAt, firstAi2AudioAt: chunkAt, delayMs: chunkAt - startedAt });
      }
      return acc;
    }, []);
    const firstAi2Measurement = ai2AudioMeasurements[0] || null;
    const averageAi2Ms = translationMeasurements.length > 0
      ? Math.round(translationMeasurements.reduce((s, v) => s + v.delayMs, 0) / translationMeasurements.length)
      : null;

    return {
      firstConversationAt: firstAi2Measurement?.startedAt ?? null,
      firstConversationEvent: firstAi2Measurement ? 'MEDIA_STT_STREAM_TRANSCRIPT' : null,
      firstTtsStartedAt: firstAi2Measurement?.firstAi2AudioAt ?? null,
      firstTtsEvent: firstAi2Measurement ? 'MEDIA_TTS_FIRST_RTP_PACKET_SENT' : null,
      ttsStartAfterConversationMs: firstAi2Measurement?.delayMs ?? null,
      averageAi2Ms,
      ai2SampleCount: translationMeasurements.length
    };
  }, [normalizedRows]);

  const latestObservedLatencyMs = useMemo(() => {
    const statusLatency = translationStatus?.latency?.latestChunkMs ?? translationStatus?.latency?.speechToTextMs;
    if (Number.isFinite(Number(statusLatency))) {
      return Number(statusLatency);
    }

    const latestTraceLatency = normalizedRows
      .slice()
      .reverse()
      .find((row) => (
        row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' &&
        (
          Number.isFinite(Number(row.details?.sttStreamLagMs)) ||
          Number.isFinite(Number(row.details?.sttStreamLatencyMs)) ||
          Number.isFinite(Number(row.details?.latencyMs))
        )
      ));

    const traceLatency = latestTraceLatency?.details?.sttStreamLagMs ??
      latestTraceLatency?.details?.sttStreamLatencyMs ??
      latestTraceLatency?.details?.latencyMs;
    return Number.isFinite(Number(traceLatency)) ? Number(traceLatency) : null;
  }, [normalizedRows, translationStatus]);

  const finalAction = normalizeAction(callRecord?.finalAction || normalizedRows.slice().reverse().find((row) => row.action)?.action);

  const headerRecord = {
    timestamp: callRecord?.timestamp || null,
    durationMs: callRecord?.durationMs ?? null,
    phoneNumber: callRecord?.phoneNumber || normalizedRows[0]?.callerNumber || '-',
    calleeNumber: callRecord?.calleeNumber || normalizedRows[0]?.calleeNumber || '-',
    carrier: callRecord?.carrier || '-',
    prepaid: callRecord?.prepaid,
    fraudScore: callRecord?.fraudScore,
    risky: callRecord?.risky,
    recentAbuse: callRecord?.recentAbuse,
    spammer: callRecord?.spammer,
    isTest: callRecord?.isTest || false,
  };

  const transcriptRows = useMemo(() => {
    const transcriptSegments = translationTranscript?.transcriptSegments || [];
    const translatedSegments = translationTranscript?.translatedSegments || [];
    // Key: `${sourceLeg}-${sequenceNo}` so caller seq=1 and callee seq=1 don't collide.
    const rowsByKey = new Map();
    const rowKey = (leg, seq) => `${leg || 'caller'}-${seq}`;
    const legFromDetails = (details) => details?.direction === 'callee_to_caller' ? 'callee' : 'caller';

    transcriptSegments.forEach((segment) => {
      const leg = segment.sourceLeg || 'caller';
      const key = rowKey(leg, segment.sequenceNo);
      rowsByKey.set(key, {
        sequenceNo: segment.sequenceNo,
        sourceLeg: leg,
        segmentId: segment.segmentId,
        transcriptText: segment.transcriptText,
        sourceLanguage: segment.sourceLanguage,
        latencyMs: segment.latencyMs,
        startedAt: segment.startedAt ? new Date(segment.startedAt).getTime() : null,
      });
    });

    translatedSegments.forEach((segment) => {
      const leg = segment.sourceLeg || 'caller';
      const key = rowKey(leg, segment.sequenceNo);
      const existing = rowsByKey.get(key) || { sequenceNo: segment.sequenceNo, sourceLeg: leg };
      rowsByKey.set(key, {
        ...existing,
        translatedSegmentId: segment.translatedSegmentId,
        translatedText: segment.translatedText,
        targetLanguage: segment.targetLanguage,
        translationLatencyMs: segment.translationLatencyMs,
        ttsLatencyMs: segment.ttsLatencyMs,
      });
    });

    // MEDIA_STT_STREAM_TRANSCRIPT fires for both directions with the same field
    // shape (transcriptText/fullTranscriptText). This is a fallback for when the
    // DB-stored transcript segment has no text (e.g. transcript storage disabled
    // for privacy) — older events without a direction field default to 'caller'.
    normalizedRows
      .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT')
      .forEach((row) => {
        const sequenceNo = row.details?.sequenceNo;
        if (sequenceNo === null || sequenceNo === undefined) {
          return;
        }
        const leg = legFromDetails(row.details);
        const key = rowKey(leg, sequenceNo);
        const existing = rowsByKey.get(key) || { sequenceNo, sourceLeg: leg };
        if (existing.transcriptText) {
          return;
        }
        rowsByKey.set(key, {
          ...existing,
          transcriptText: row.details?.fullTranscriptText || row.details?.transcriptText || row.details?.interimTranscript || null,
          sourceLanguage: existing.sourceLanguage || row.details?.sourceLanguage || null,
          latencyMs: existing.latencyMs ?? row.details?.latencyMs ?? row.details?.sttStreamLagMs ?? null,
          sourceFromTrace: true,
        });
      });

    // Translation delay is end-of-turn -> translated text ready. Prefer the explicit
    // completion marker; older traces use playback queueing as the closest equivalent.
    const endOfTurnByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_END_OF_TURN_PENDING' && row.details?.sequenceNo != null)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), row.timestamp])
    );
    const transcriptReleasedByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.transcriptEmittedAt) ?? row.timestamp
        ])
        .filter(([, releasedAt]) => Number.isFinite(releasedAt))
    );
    const speechActivityEndedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.speechActivityEndedAt)
        ])
        .filter(([, endedAt]) => Number.isFinite(endedAt))
    );
    const translationCompletedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_TRANSLATION_COMPLETED' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.translationCompletedAt) ?? row.timestamp
        ])
    );
    const queuedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_PLAYBACK_QUEUED' && row.details?.sequenceNo != null && row.timestamp != null)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), row.timestamp])
    );
    const ttsStartedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_TTS_STARTED' && row.details?.sequenceNo != null && row.details?.ttsStartedAt)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), parseTraceTime(row.details.ttsStartedAt)])
    );
    const speechStartedAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_STT_STREAM_TRANSCRIPT' && row.details?.sequenceNo != null)
        .map((row) => [
          rowKey(legFromDetails(row.details), row.details.sequenceNo),
          parseTraceTime(row.details.callerSpeechStartedAt) ?? parseTraceTime(row.details.receivedAt)
        ])
        .filter(([, t]) => Number.isFinite(t))
    );
    const firstRtpSentAtByKey = new Map(
      normalizedRows
        .filter((row) => row.eventName === 'MEDIA_TTS_FIRST_RTP_PACKET_SENT' && row.details?.sequenceNo != null && row.details?.firstRtpPacketSentAt)
        .map((row) => [rowKey(legFromDetails(row.details), row.details.sequenceNo), parseTraceTime(row.details.firstRtpPacketSentAt)])
        .filter(([, t]) => Number.isFinite(t))
    );
    for (const [key, row] of rowsByKey) {
      const releasedAt = transcriptReleasedByKey.get(key);
      const endedAt = speechActivityEndedAtByKey.get(key) ?? endOfTurnByKey.get(key) ?? releasedAt;
      const startedAt = speechStartedAtByKey.get(key) ?? endedAt;
      const speakAt = firstRtpSentAtByKey.get(key) ?? translationCompletedAtByKey.get(key) ?? queuedAtByKey.get(key);
      let updated = row;
      if (Number.isFinite(startedAt) && Number.isFinite(speakAt) && speakAt >= startedAt) {
        updated = { ...updated, translationDelayMs: speakAt - startedAt };
      }
      // "Speaker stopped": the voice-activity end when STT reported one (caller streaming).
      // The callee buffered-fallback path has none, so fall back to when the end-of-turn
      // wait started. That is a little after the speaker really stopped, so the delay
      // shown for it is a lower bound and is marked with "≥".
      const audibleAt = firstRtpSentAtByKey.get(key) ?? queuedAtByKey.get(key) ?? translationCompletedAtByKey.get(key);
      const speechEndedAt = speechActivityEndedAtByKey.get(key);
      const afterSpeechApprox = !Number.isFinite(speechEndedAt);
      const afterSpeechDelayMs = computeAfterSpeechDelayMs(
        afterSpeechApprox ? endOfTurnByKey.get(key) : speechEndedAt,
        audibleAt
      );
      if (afterSpeechDelayMs !== null) {
        updated = { ...updated, afterSpeechDelayMs, afterSpeechApprox };
      }
      const queuedAt = queuedAtByKey.get(key);
      const ttsStartedAt = ttsStartedAtByKey.get(key);
      if (Number.isFinite(queuedAt) && Number.isFinite(ttsStartedAt) && ttsStartedAt - queuedAt > 2000) {
        updated = { ...updated, queueWaitMs: ttsStartedAt - queuedAt };
      }
      rowsByKey.set(key, updated);
    }

    return Array.from(rowsByKey.values()).sort((a, b) => {
      const aTime = Number.isFinite(a.startedAt) ? a.startedAt : null;
      const bTime = Number.isFinite(b.startedAt) ? b.startedAt : null;
      if (aTime !== null && bTime !== null) return aTime - bTime;
      if (aTime !== null) return -1;
      if (bTime !== null) return 1;
      const left = Number.isFinite(Number(a.sequenceNo)) ? Number(a.sequenceNo) : Number.MAX_SAFE_INTEGER;
      const right = Number.isFinite(Number(b.sequenceNo)) ? Number(b.sequenceNo) : Number.MAX_SAFE_INTEGER;
      if (left !== right) return left - right;
      return (a.sourceLeg || 'caller').localeCompare(b.sourceLeg || 'caller');
    });
  }, [normalizedRows, translationTranscript]);

  const transcriptTextMissing = transcriptRows.length > 0 &&
    transcriptRows.every((segment) => !segment.transcriptText && !segment.translatedText);

  const copyJson = async () => {
    const text = JSON.stringify(traceRows, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
    } catch (copyError) {
      // no-op fallback
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        SIP Trace Viewer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Read-only trace details for a call history record.
      </Typography>

      {loading ? (
        <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading trace...</Typography>
        </Paper>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper sx={{ p: 0, mb: 2.5, overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Related Call History Record
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                {callerDisclaimerPlayback && (
                  <Chip
                    label={callerDisclaimerPlayback.label}
                    color={callerDisclaimerPlayback.color}
                    size="small"
                    variant="outlined"
                  />
                )}
                <Chip label={`Call ID: ${traceId || '-'}`} variant="outlined" size="small" />
                <Chip
                  label={`Call GUID: ${normalizedRows.find((row) => row.details?.callGuid)?.details?.callGuid || '-'}`}
                  title="Stable tracking ID for this call. Calls recorded before it was introduced show '-'."
                  variant="outlined"
                  size="small"
                  sx={{ userSelect: 'text' }}
                />
              </Box>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Caller</TableCell>
                  <TableCell>Callee</TableCell>
                  <TableCell>Carrier</TableCell>
                  <TableCell>Prepaid</TableCell>
                  <TableCell>Fraud Score</TableCell>
                  <TableCell>Risky</TableCell>
                  <TableCell>Recent Abuse</TableCell>
                  <TableCell>Spammer</TableCell>
                  <TableCell>Final Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {formatThailandTime(headerRecord.timestamp)}
                      {headerRecord.isTest && (
                        <Chip label="TEST" color="info" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDurationMs(headerRecord.durationMs)}</TableCell>
                  <TableCell>{headerRecord.phoneNumber}</TableCell>
                  <TableCell>{headerRecord.calleeNumber}</TableCell>
                  <TableCell>{headerRecord.carrier || '-'}</TableCell>
                  <TableCell>
                    {headerRecord.prepaid === true ? (
                      <Chip label="Yes" color="warning" size="small" />
                    ) : headerRecord.prepaid === false ? (
                      <Chip label="No" color="default" size="small" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={headerRecord.fraudScore !== null && headerRecord.fraudScore !== undefined ? headerRecord.fraudScore : '-'}
                      color={headerRecord.fraudScore >= 75 ? 'error' : headerRecord.fraudScore >= 50 ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {headerRecord.risky === true ? (
                      <Chip label="Yes" color="error" size="small" />
                    ) : headerRecord.risky === false ? (
                      <Chip label="No" color="success" size="small" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {headerRecord.recentAbuse === true ? (
                      <Chip label="Yes" color="error" size="small" />
                    ) : headerRecord.recentAbuse === false ? (
                      <Chip label="No" color="success" size="small" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {headerRecord.spammer === true ? (
                      <Chip label="Yes" color="error" size="small" />
                    ) : headerRecord.spammer === false ? (
                      <Chip label="No" color="success" size="small" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={finalAction || '-'} color={getActionColor(finalAction)} size="small" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          {translationStatus && (
            <Paper sx={{ p: 0, mb: 2.5, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Translation Session
                </Typography>
                <Chip
                  label={formatTranslationStatus(translationStatus.status)}
                  color={getTranslationStatusColor(translationStatus.status)}
                  size="small"
                />
              </Box>
              <Divider />
              <Box sx={{
                p: 2,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                gap: 1.5,
              }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Detected language</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatLanguage(translationStatus.detectedSourceLanguage)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Target language</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatLanguage(translationStatus.targetLanguage)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Initial caller gender</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {translationStatus.callerDetectedGender ? formatVoiceProfile(translationStatus.callerDetectedGender) : '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Initial callee gender</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {translationStatus.calleeDetectedGender ? formatVoiceProfile(translationStatus.calleeDetectedGender) : '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Audio mode</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTranslationStatus(translationStatus.audioMode)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Latest latency</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatLatencyMs(latestObservedLatencyMs)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">First conversation</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatMeasurementTimestamp(timingMeasurements.firstConversationAt, firstTimestamp)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Average Translation Time</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {timingMeasurements.averageAi2Ms != null ? formatLatencyMs(timingMeasurements.averageAi2Ms) : '-'}
                  </Typography>
                  {timingMeasurements.ai2SampleCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      avg of {timingMeasurements.ai2SampleCount} turn{timingMeasurements.ai2SampleCount !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">AI2 conversation start</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatRelativeMs(timingMeasurements.ttsStartAfterConversationMs)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Session duration</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatDurationMs(translationStatus.latency?.sessionAgeMs)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Estimated provider cost</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatEstimatedUsd(translationStatus.providerCost?.estimatedCostUsd)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">before provider free tier</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">STT processed audio</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatUsageQuantity(translationStatus.providerCost?.stages?.stt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Translation usage</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatUsageQuantity(translationStatus.providerCost?.stages?.translation)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">TTS usage</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatUsageQuantity(translationStatus.providerCost?.stages?.tts)}
                  </Typography>
                  {translationStatus.providerCost?.stages?.tts?.cacheHits > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {translationStatus.providerCost.stages.tts.cacheHits} cache hit{translationStatus.providerCost.stages.tts.cacheHits !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
                {translationStatus.skipReason && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Skip reason</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTranslationStatus(translationStatus.skipReason)}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 0, mb: 2.5, overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Tabs
                value={detailTab}
                onChange={(event, value) => setDetailTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 42 }}
              >
                <Tab value="timeline" label={`Timeline (${translationTimelineRows.length})`} sx={{ minHeight: 42 }} />
                <Tab value="transcript" label={`Transcript (${transcriptRows.length})`} sx={{ minHeight: 42 }} />
                <Tab value="rhino" label={`Rhino Trace (${tasTraceRows.length})`} sx={{ minHeight: 42 }} />
              </Tabs>
              {detailTab === 'rhino' && (
                <Button variant="outlined" size="small" onClick={copyJson} sx={{ flexShrink: 0 }}>
                  COPY JSON
                </Button>
              )}
            </Box>
            <Divider />
          </Paper>

          {detailTab === 'timeline' && (
            <Paper sx={{ p: 0, mb: 2.5, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Translation Session Timeline ({translationTimelineRows.length} events)
                </Typography>
              </Box>
              <Divider />
              {translationTimelineRows.length === 0 ? (
                <Alert severity="info" sx={{ m: 2 }}>No translation timeline events found for this call.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time (ms)</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {translationTimelineRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMs(row.relativeMs)}</TableCell>
                        <TableCell>{formatTranslationStatus(row.event)}</TableCell>
                        <TableCell><Chip label={row.status} size="small" /></TableCell>
                        <TableCell>{row.details}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          )}

          {detailTab === 'transcript' && (
            <Paper sx={{ p: 0, mb: 2.5, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Translation Transcript
                </Typography>
              </Box>
              <Divider />
              {translationTranscriptError && transcriptRows.length === 0 ? (
                <Alert severity="info" sx={{ m: 2 }}>
                  {translationTranscriptError}
                </Alert>
              ) : transcriptRows.length === 0 ? (
                <Alert severity="info" sx={{ m: 2 }}>
                  No transcript rows were stored for this call.
                </Alert>
              ) : (
                <>
                  {transcriptTextMissing && (
                    <Alert severity="info" sx={{ m: 2 }}>
                      Transcript rows exist, but the source and translated text were not stored for this call. Enable transcript storage before the call to keep the script.
                    </Alert>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Seq</TableCell>
                        <TableCell>Direction</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Translated</TableCell>
                        <TableCell title="Time from first audio sent to STT until first TTS packet reaches listener (speech + STT + translation + TTS)">AI2 Speak Delay</TableCell>
                        <TableCell title="Time from the moment the speaker stopped talking until the translated audio starts playing. Does not include how long the speaker talked. Shown with ≥ when the exact stop time was not recorded, so the real delay is at least this long.">After Speaker Stops</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transcriptRows.map((segment) => (
                        <TableRow key={`${segment.sourceLeg || 'caller'}-${segment.segmentId || segment.translatedSegmentId || segment.sequenceNo}`}>
                          <TableCell>{segment.sequenceNo}</TableCell>
                          <TableCell>
                            {segment.sourceLeg === 'callee' ? (
                              <Chip label="Callee → Caller" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            ) : (
                              <Chip label="Caller → Callee" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxWidth: 520 }}>
                            {segment.transcriptText || '-'}
                            {segment.sourceFromTrace && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Recovered from STT trace
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxWidth: 620 }}>
                            {segment.translatedText || '-'}
                          </TableCell>
                          <TableCell>
                            {formatLatencyMs(segment.translationDelayMs ?? segment.translationLatencyMs)}
                            {Number.isFinite(segment.queueWaitMs) && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                +{Math.round(segment.queueWaitMs / 1000)}s queued
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {segment.afterSpeechApprox && Number.isFinite(segment.afterSpeechDelayMs) ? '≥' : ''}
                            {formatLatencyMs(segment.afterSpeechDelayMs)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Paper>
          )}

          {detailTab === 'rhino' && (
          <Paper sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                📜 Rhino TAS Full Trace ({tasTraceRows.length} events)
              </Typography>
            </Box>
            <Divider />
              {tasTraceRows.length === 0 ? (
                <Alert severity="warning" sx={{ m: 2 }}>No Rhino TAS events found for this call.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time (ms)</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasTraceRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMs(row.relativeMs)}</TableCell>
                        <TableCell>{row.event}</TableCell>
                        <TableCell>
                          <Chip label={row.status} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.details}</Typography>
                          {row.payload && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {JSON.stringify(row.payload)}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </Paper>
          )}
        </>
      )}
    </Box>
  );
}
